import { useCallback, useEffect, useState } from "react";
import type { StudentSummary } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { studentCountText } from "../../lib/plurals.ts";
import { filterByQuery } from "../../lib/search.ts";
import { SearchField } from "../../lib/SearchField.tsx";
import { LoadingRows } from "../../lib/loading.tsx";
import { NameDialog } from "../shell/NameDialog.tsx";
import { StudentsPanel } from "./StudentsPanel.tsx";

/**
 * Ученики: раздел кабинета.
 *
 * Отдельный экран, а не половина главной (решение владельца 14.08.2026):
 * учеников заводят раз в жизни, а уроки создают каждый день, и вперемешку они
 * мешали друг другу.
 *
 * ⚠️ Список уроков экран больше НЕ грузит (18.08.2026). Он грузился ровно
 * ради четырёх занятий в карточке ученика, а от них список и плыл: у одного
 * четыре даты, у соседнего ни одной. Занятия ученика целиком есть на его
 * карточке, а здесь вместо них колонка «следующий урок», которая приезжает
 * вместе со списком учеников. На канале 128 кбит/с (раздел 3.1 CLAUDE.md) это
 * минус целый круг по сети при каждом открытии экрана.
 *
 * ⚠️ Заготовки экран тоже больше не грузит (17.09.2026). Он грузил их ради
 * полосы «создать уроки нескольким», а она ушла вместе с уроком без даты:
 * занятие заводится в расписании и всегда встаёт на дату и время. Запрос был
 * второй по счёту на экране, где ни одна строка списка о заготовках не
 * говорит.
 *
 * ⚠️ **Заведение и поиск поменялись местами 14.09.2026** (подход 2е переноса
 * дизайн-системы, решение владельца 3 раздела 2.4 задания). До этого дня над
 * списком стояла форма из одного поля «Имя ученика» с кнопкой «Завести», и
 * агент, проходивший стенд, принял её за поиск: одиночное поле с плейсхолдером
 * и кнопкой рядом означает фильтр в любом другом приложении. Настоящего поиска
 * при этом не было ни на одном экране продукта. Теперь место над списком занял
 * поиск, а заведение стало главным действием экрана и открывает окно.
 *
 * ⚠️ **Коды выданных ссылок живут ЗДЕСЬ, а не в панели списка.** Пишут их двое:
 * окно заведения (сервер отдаёт код ровно один раз, в ответе на создание) и
 * кнопка «Новая ссылка» в строке. Общий владелец у них один, и это экран:
 * держи карту в панели, окну пришлось бы дотягиваться до чужого состояния.
 */
export function StudentsPage() {
  const [students, setStudents] = useState<StudentSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  /**
   * Коды, показанные в этой сессии экрана. Перезагрузка их теряет, и это норма:
   * в базе от ссылки только SHA-256, и второй раз сервер её не покажет.
   */
  const [links, setLinks] = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    try {
      const ss = await api.listStudents();
      setStudents(ss.students);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = (displayName: string) => {
    setBusy(true);
    setAddErr(null);
    api.createStudent(displayName).then(
      (created) => {
        setBusy(false);
        setAdding(false);
        setLinks((prev) => ({ ...prev, [created.userId]: created.inviteUrl }));
        /*
          ⚠️ Запрос снимается заведением. Иначе заведённый под набранным
          поиском ученик не попал бы в отобранное, и вместе с ним пропала бы
          его ссылка, которую сервер второй раз не покажет.
        */
        setQuery("");
        void reload();
      },
      (e: unknown) => {
        setBusy(false);
        setAddErr(e instanceof Error ? e.message : String(e));
      },
    );
  };

  const all = students ?? [];
  const shown = filterByQuery(all, query, (s) => s.displayName);

  return (
    <main className="home home--list">
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Ученики</h1>
          {students !== null && (
            <span className="head__n">{studentCountText(all.length)}</span>
          )}
          {/* Главное действие экрана: сюда приходят завести человека и выдать
              ему ссылку */}
          <span className="head__act">
            <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
              Завести ученика
            </button>
          </span>
        </div>
      </header>

      {err && <p className="err">{err}</p>}

      {/* Полоса появляется вместе со списком: искать в том, чего ещё нет,
          нечем, а поле поиска над пустым местом обещает несуществующее */}
      {students !== null && all.length > 0 && (
        <div className="bar">
          <SearchField label="Поиск по имени" value={query} onChange={setQuery} />
          {/* Сколько отобралось. ⚠️ Считаются СТРОКИ СПИСКА, а не работа
              преподавателя (раздел 5.10 CLAUDE.md) */}
          {query.trim() !== "" && (
            <span className="bar__n">
              {shown.length} из {all.length}
            </span>
          )}
        </div>
      )}

      {students === null ? (
        <section className="card">
          <LoadingRows list="list--students" cells={5} narrow="page" />
        </section>
      ) : (
        <StudentsPanel
          students={all}
          shown={shown}
          query={query}
          onClearQuery={() => setQuery("")}
          onAdd={() => setAdding(true)}
          links={links}
          onLink={(userId, url) => setLinks((prev) => ({ ...prev, [userId]: url }))}
          onChanged={() => void reload()}
        />
      )}

      {adding && (
        <NameDialog
          title="Новый ученик"
          label="Имя ученика"
          submit="Завести"
          maxLength={80}
          hint="Ссылка для входа появится в строке ученика и показывается один раз."
          err={addErr}
          busy={busy}
          onSubmit={add}
          onClose={() => {
            setAdding(false);
            setAddErr(null);
          }}
        />
      )}
    </main>
  );
}
